import type { KyInstance } from 'ky';

import { ensureError } from '@enterprise/core';
import type { OperationResultWithData } from '@enterprise/types';

import { Uri } from '#constants/uri';

import type { HasPermission } from './types/has-permission.type';

/**
 * Checks if the current user has the specified permissions by making a GET request
 * to the authentication service.
 *
 * @param instance - The Ky HTTP client instance used to make the request.
 * @param permissions - An array of permission strings to check.
 * @returns A promise that resolves to an object mapping each permission to a boolean
 *          indicating whether the user has that permission.
 * @throws Throws an error if the request fails or an unexpected error occurs.
 */
export async function hasPermission(instance: KyInstance, permissions: string[]) {
  try {
    const searchParams = new URLSearchParams();

    for (const permission of permissions) {
      searchParams.append('permission', permission);
    }

    return await instance
      .get<OperationResultWithData<HasPermission[]>>(Uri.authentication.permission, {
        searchParams,
      })
      .json();
  } catch (e) {
    const error = ensureError(e);

    throw error;
  }
}
